import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, CheckCircle, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { C } from "./profileTheme";
import { InfoTip } from "./InfoTip";
import { useTranslation } from "react-i18next";
import { showError, showSuccess } from "@/lib/toast";
import { useAccount, useWriteContract } from "wagmi";
import { simulateContract, switchChain } from "@wagmi/core";
import { parseEther } from "viem";
import { useQueryClient } from "@tanstack/react-query";
import { formatNumberInput, formatEtherWithDecimals } from "@/lib/utils";
import { parseChainError } from "@/lib/chainError";
import { bscChainId, wagmiConfig } from "@/config/wagmi";
import {
  CCC_AIF_ADDRESS,
  CCC_FUSD_ADDRESS,
  USDT_ADDRESS,
  WITHDRAW_PROXY_CONTRACT_ADDRESS,
} from "@/config/env";
import { useSubmitWithdrawOrDepositTx } from "@/hooks/api/useSubmitWithdrawOrDepositTx";
import { WithdrawContractAbi } from "@/abi/WithdrawContractAbi.abi";
import { Spinner } from "@/components/ui/spinner";
import type { BalanceItem } from "@/api/types";
import { getTokenName } from "@/config/common";
import { usePlatFormConfig } from "@/hooks/api/usePlatFormConfig";

const MIN_WITHDRAW_USDT = 10;

export function WithdrawDialog({
  open,
  onClose,
  usdtBalanceData,
}: {
  open: boolean;
  onClose: () => void;
  /** 个人页 `getTokenBalanceData(USDT_ADDRESS)`，可用为 `canUseUsdtAmount` */
  usdtBalanceData?: BalanceItem | null;
}) {
  const { t } = useTranslation();
  const { address: walletAddress } = useAccount();
  const queryClient = useQueryClient();
  const { writeContractAsync } = useWriteContract();
  const { data: platFormConfig } = usePlatFormConfig();

  const { mutateAsync: submitWithdrawTx } = useSubmitWithdrawOrDepositTx();

  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [step, setStep] = useState<"form" | "pending" | "success">("form");
  const [isLoading, setIsLoading] = useState(false);
  const numAmount = parseFloat(amount) || 0;
  const feeRate = (platFormConfig?.withdrawUsdtFeeRatio ?? 0) / 1000;
  const feeUsd = numAmount * feeRate;
  // TSK 未发布阶段：先固定为 0，后续发币后接入真实价格和余额
  const mockTskPriceUsd = 0;
  const tskBalanceUsd = 0 * mockTskPriceUsd;
  const hasTsk = tskBalanceUsd >= feeUsd && feeUsd > 0;
  const payMethod = hasTsk ? "TSK" : "USDT";
  const actualReceive = payMethod === "USDT" ? numAmount - feeUsd : numAmount;

  const availableWei = BigInt(usdtBalanceData?.canUseUsdtAmount ?? "0");
  const maxAmount = Number(formatEtherWithDecimals(availableWei));

  const handleClose = () => {
    setStep("form");
    setAmount("");
    setAddress("");
    setIsLoading(false);
    onClose();
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && !/^\d*\.?\d*$/.test(value)) return;
    const [formatted, isGtMax] = formatNumberInput(value, maxAmount);
    setAmount(formatted);
    if (isGtMax) {
      showError(t("profilePage.depositDialog.insufficientBalance"));
    }
  };

  const setQuickAmount = (pct: number) => {
    const val = (maxAmount * pct).toFixed(2);
    setAmount(val);
  };

  const handleConfirm = async () => {
    if (!walletAddress) {
      showError(t("profilePage.depositDialog.connectWalletFirst"));
      return;
    }
    if (!WITHDRAW_PROXY_CONTRACT_ADDRESS) {
      showError(t("profilePage.withdrawDialog.contractNotConfigured"));
      return;
    }
    if (!amount || numAmount <= 0) {
      showError(t("profilePage.withdrawDialog.enterAmount"));
      return;
    }
    if (numAmount < MIN_WITHDRAW_USDT) {
      showError(
        t("profilePage.withdrawDialog.minAmountError", {
          min: MIN_WITHDRAW_USDT,
          tokenNameUsdt: getTokenName(USDT_ADDRESS),
        })
      );
      return;
    }

    let parsedWei: bigint;
    try {
      parsedWei = parseEther(amount);
    } catch {
      showError(t("profilePage.withdrawDialog.enterAmount"));
      return;
    }

    if (parsedWei > availableWei) {
      showError(t("profilePage.depositDialog.insufficientBalance"));
      return;
    }

    const trueWithdrawAmount =
      parsedWei > availableWei ? availableWei : parsedWei;

    try {
      await switchChain(wagmiConfig, { chainId: bscChainId });
      setIsLoading(true);
      const { request } = await simulateContract(wagmiConfig, {
        address: WITHDRAW_PROXY_CONTRACT_ADDRESS as `0x${string}`,
        abi: WithdrawContractAbi,
        functionName: "withdrawRequest",
        args: [USDT_ADDRESS as `0x${string}`, trueWithdrawAmount],
      });
      const tx = await writeContractAsync(request);
      await submitWithdrawTx({
        txId: tx,
        opCode: 2,
        amount: trueWithdrawAmount.toString(),
        coinContract: USDT_ADDRESS,
      });
      showSuccess(t("profilePage.withdrawDialog.success"));
      queryClient.invalidateQueries({ queryKey: ["balanceV2"] });
      queryClient.invalidateQueries({
        queryKey: ["withdrawAndRechargeRecord"],
      });
      handleClose();
    } catch (error) {
      console.error("withdrawErr", error);
      const message = parseChainError(
        error,
        "profilePage.withdrawDialog.withdrawFailed"
      );
      showError(t(message));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-sm mx-auto"
        onOpenAutoFocus={event => {
          // 阻止 Dialog 打开时自动聚焦首个可聚焦元素（避免唤起移动端输入法）
          event.preventDefault();
        }}
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(34,197,94,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>
            {t("profilePage.withdrawDialog.title", {
              tokenNameUsdt: getTokenName(USDT_ADDRESS),
            })}
          </DialogTitle>
        </DialogHeader>
        <AnimatePresence mode="wait">
          {step === "form" && (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              <div
                className="rounded-lg p-3 text-xs"
                style={{ background: C.card, border: `1px solid ${C.border}` }}
              >
                <div className="flex justify-between">
                  <span style={{ color: C.dim }}>
                    {t("profilePage.withdrawDialog.withdrawableBalance")}
                  </span>
                  <span className="mono-num">
                    {formatEtherWithDecimals(availableWei)}{" "}
                    {getTokenName(USDT_ADDRESS)}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-xs mb-2" style={{ color: C.dim }}>
                  {t("profilePage.withdrawDialog.amount")}
                </p>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder={t("profilePage.withdrawDialog.placeholder")}
                    className="w-full px-3 py-3 rounded-lg text-sm mono-num outline-none"
                    style={{
                      background: C.card,
                      border: `1px solid ${C.border}`,
                      color: C.text,
                    }}
                    disabled={isLoading}
                  />
                  <span
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                    style={{ color: C.dim }}
                  >
                    {getTokenName(USDT_ADDRESS)}
                  </span>
                </div>
                <p className="text-[11px] mt-1.5" style={{ color: C.dim }}>
                  {t("profilePage.withdrawDialog.minAmountHint", {
                    min: MIN_WITHDRAW_USDT,
                    tokenNameUsdt: getTokenName(USDT_ADDRESS),
                  })}
                </p>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: "25%", pct: 0.25 },
                  { label: "50%", pct: 0.5 },
                  { label: "75%", pct: 0.75 },
                  { label: t("profilePage.depositDialog.all"), pct: 1 },
                ].map(({ label, pct }) => (
                  <button
                    key={label}
                    type="button"
                    disabled={isLoading}
                    onClick={() =>
                      pct < 1
                        ? setQuickAmount(pct)
                        : setAmount(formatEtherWithDecimals(availableWei))
                    }
                    className="py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: C.card,
                      border: `1px solid ${C.border}`,
                      color: C.muted,
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {/* <div>
                <p className="text-xs mb-2" style={{ color: C.dim }}>
                  提现地址
                </p>
                <input
                  value={address}
                  onChange={e => setAddress(e.target.value)}
                  placeholder="0x..."
                  className="w-full px-3 py-3 rounded-lg text-sm mono-num outline-none"
                  style={{
                    background: C.card,
                    border: `1px solid ${C.border}`,
                    color: C.text,
                  }}
                />
              </div> */}
              {numAmount > 0 && feeRate > 0 && (
                <div
                  className="rounded-xl p-3.5 text-xs space-y-2"
                  style={{
                    background: C.card,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <div className="flex justify-between">
                    <span style={{ color: C.dim }}>
                      {t("profilePage.withdrawDialog.fee")} ({feeRate * 100}%)
                      {/* <InfoTip
                        text={t("profilePage.withdrawDialog.feeTip", {
                          tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
                        })}
                      /> */}
                    </span>
                    <span className="mono-num" style={{ color: C.muted }}>
                      ≈ ${feeUsd.toFixed(2)}
                    </span>
                  </div>
                  <div className="h-px" style={{ background: C.border }} />
                  <div className="flex justify-between">
                    <span style={{ color: C.dim }}>
                      {t("profilePage.withdrawDialog.paymentMethod")}
                    </span>
                    <span className="flex items-center gap-1.5">
                      {hasTsk ? (
                        <span
                          className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold"
                          style={{
                            background: "rgba(34,197,94,0.10)",
                            border: "1px solid rgba(34,197,94,0.25)",
                            color: C.green,
                          }}
                        >
                          {t("profilePage.withdrawDialog.payWithTsk", {
                            tokenNameTsk: getTokenName(CCC_AIF_ADDRESS),
                          })}
                        </span>
                      ) : (
                        <span
                          className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold"
                          style={{
                            background: "rgba(245,158,11,0.10)",
                            border: "1px solid rgba(245,158,11,0.25)",
                            color: C.amber,
                          }}
                        >
                          {t("profilePage.withdrawDialog.payWithUsdt", {
                            tokenNameUsdt: getTokenName(USDT_ADDRESS),
                          })}
                        </span>
                      )}
                    </span>
                  </div>
                  {hasTsk ? (
                    <>
                      <div className="flex justify-between">
                        <span style={{ color: C.dim }}>
                          {t("profilePage.withdrawDialog.tskBalanceInUsd", {
                            tokenNameTsk: getTokenName(CCC_AIF_ADDRESS),
                          })}
                        </span>
                        <span className="mono-num" style={{ color: C.green }}>
                          ${tskBalanceUsd.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: C.dim }}>
                          {t("profilePage.withdrawDialog.actualReceived")}
                        </span>
                        <span
                          className="mono-num font-semibold"
                          style={{ color: C.green }}
                        >
                          {actualReceive.toFixed(2)}{" "}
                          {getTokenName(USDT_ADDRESS)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* <div
                      className="rounded-lg p-2"
                      style={{
                        background: "rgba(245,158,11,0.06)",
                        border: "1px solid rgba(245,158,11,0.12)",
                      }}
                    >
                      <div
                        className="flex items-start gap-1.5 text-[10px]"
                        style={{ color: C.amber }}
                      >
                        <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                        <span>
                          TSK 余额不足（当前 ${tskBalanceUsd.toFixed(2)}），系统将自动从提现金额中扣除手续费
                        </span>
                      </div>
                    </div> */}
                      <div className="flex justify-between">
                        <span style={{ color: C.dim }}>
                          {t("profilePage.withdrawDialog.actualReceived")}
                        </span>
                        <span
                          className="mono-num font-semibold"
                          style={{ color: C.amber }}
                        >
                          {actualReceive.toFixed(2)}{" "}
                          {getTokenName(USDT_ADDRESS)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
              <button
                type="button"
                className="w-full py-3 rounded-lg font-bold text-sm btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                // disabled={!amount || !address || numAmount <= 0}
                disabled={isLoading}
                onClick={() => {
                  void handleConfirm();
                  // setStep("pending");
                  // setTimeout(() => setStep("success"), 1500);
                }}
              >
                {isLoading ? <Spinner className="size-5" /> : null}
                {t("profilePage.withdrawDialog.confirm")}
              </button>
            </motion.div>
          )}
          {/* {step === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center py-8 gap-3"
            >
              <RefreshCw
                size={28}
                className="animate-spin"
                style={{ color: C.primary }}
              />
              <p className="text-sm" style={{ color: C.muted }}>
                处理中...
              </p>
            </motion.div>
          )} */}
          {/* {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center py-6 gap-3"
            >
              <CheckCircle size={40} style={{ color: C.green }} />
              <p className="font-semibold">提现成功！</p>
              <p className="text-sm" style={{ color: C.muted }}>
                资金已发送至您的地址
              </p>
              <button
                className="w-full py-3 rounded-lg font-bold text-sm btn-primary"
                onClick={handleClose}
              >
                完成
              </button>
            </motion.div>
          )} */}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
